import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Cloud,
  CloudOff,
  Download,
  Image as ImageIcon,
  Loader2,
  Plus,
  Settings,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ImagePickerPopover } from "@/components/admin/image-picker-popover";
import { htmlToMarkdown, TiptapEditor } from "@/components/admin/tiptap-editor";
import type { UploadResult } from "@/components/admin/tiptap-file-upload-extension";
import { Button } from "@/components/button";
import { usePageTitle } from "@/hooks/use-page-title";

type PostType = "blog" | "case-study";

interface Post {
  id: string;
  type: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  featured_image_url: string | null;
  status: string;
  author_name: string | null;
  author_avatar_url: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  categories: { id: string; name: string; singular_name: string | null; slug: string }[];
  // Case study specific fields
  client_name: string | null;
  client_logo_url: string | null;
  client_industry: string | null;
  services_used: string | null;
  testimonial_quote: string | null;
  testimonial_author: string | null;
  testimonial_role: string | null;
  before_image_url: string | null;
  after_image_url: string | null;
  project_template_id: string | null;
  live_url: string | null;
  prompt: string | null;
}

interface Category {
  id: string;
  name: string;
  singular_name: string | null;
  slug: string;
  type: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function PostEditor() {
  const { type, id } = useParams<{ type: PostType; id: string }>();
  const isNew = id === "new";
  const postType = type === "case-study" ? "case_study" : "blog";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorAvatarUrl, setAuthorAvatarUrl] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [status, setStatus] = useState<"draft" | "published">("draft");

  // Case study specific fields
  const [clientName, setClientName] = useState("");
  const [clientLogoUrl, setClientLogoUrl] = useState("");
  const [clientIndustry, setClientIndustry] = useState("");
  const [servicesUsed, setServicesUsed] = useState<string[]>([]);
  const [testimonialQuote, setTestimonialQuote] = useState("");
  const [testimonialAuthor, setTestimonialAuthor] = useState("");
  const [testimonialRole, setTestimonialRole] = useState("");
  const [beforeImageUrl, setBeforeImageUrl] = useState("");
  const [afterImageUrl, setAfterImageUrl] = useState("");
  const [projectTemplateId, setProjectTemplateId] = useState("");
  const [liveUrl, setLiveUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [newService, setNewService] = useState("");

  // Dynamic page title
  const editorTitle = title || (isNew ? "New Post" : "Edit Post");
  usePageTitle(editorTitle);

  // UI state
  const [showSettings, setShowSettings] = useState(true);
  const [showSeo, setShowSeo] = useState(false);
  const [showCategoryInput, setShowCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track initial load to prevent auto-save on first render
  const initialLoadRef = useRef(true);
  const currentPostIdRef = useRef<string | null>(null);

  // Fetch existing post
  const { data: postData, isLoading: postLoading } = useQuery({
    queryKey: ["admin-post", id],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/admin/posts/${id}`);
      if (!res.ok) throw new Error("Failed to fetch post");
      return res.json() as Promise<{ data: Post }>;
    },
    enabled: !isNew && !!id,
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ["admin-categories", postType],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/admin/categories?type=${postType}`);
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json() as Promise<{ data: Category[] }>;
    },
  });

  // Load post data
  useEffect(() => {
    if (postData?.data) {
      const post = postData.data;
      initialLoadRef.current = true;
      setTitle(post.title || "");
      setSlug(post.slug || "");
      setContent(post.content || "");
      setExcerpt(post.excerpt || "");
      setFeaturedImageUrl(post.featured_image_url || "");
      setAuthorName(post.author_name || "");
      setAuthorAvatarUrl(post.author_avatar_url || "");
      setSeoTitle(post.seo_title || "");
      setSeoDescription(post.seo_description || "");
      setSeoKeywords(post.seo_keywords || "");
      setSelectedCategories(post.categories.map((c) => c.id));
      setStatus(post.status as "draft" | "published");
      // Case study fields
      setClientName(post.client_name || "");
      setClientLogoUrl(post.client_logo_url || "");
      setClientIndustry(post.client_industry || "");
      setServicesUsed(post.services_used ? JSON.parse(post.services_used) : []);
      setTestimonialQuote(post.testimonial_quote || "");
      setTestimonialAuthor(post.testimonial_author || "");
      setTestimonialRole(post.testimonial_role || "");
      setBeforeImageUrl(post.before_image_url || "");
      setAfterImageUrl(post.after_image_url || "");
      setProjectTemplateId(post.project_template_id || "");
      setLiveUrl(post.live_url || "");
      setPrompt(post.prompt || "");
      setHasUnsavedChanges(false);
      // Reset initial load flag after a short delay
      setTimeout(() => {
        initialLoadRef.current = false;
      }, 100);
    }
  }, [postData]);

  // Auto-generate slug from title
  useEffect(() => {
    if (isNew && title && !slug) {
      setSlug(slugify(title));
    }
  }, [isNew, title, slug]);

  // Track unsaved changes
  useEffect(() => {
    if (initialLoadRef.current) return;
    if (title || content) {
      setHasUnsavedChanges(true);
    }
  }, [
    title,
    slug,
    content,
    excerpt,
    featuredImageUrl,
    authorName,
    seoTitle,
    seoDescription,
    seoKeywords,
    selectedCategories,
  ]);

  // Upload handler for paste/drop in editor
  const handleEditorUpload = useCallback(
    async (files: File[]): Promise<UploadResult[]> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const res = await authenticatedFetch("/api/admin/media/upload", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = (await res.json()) as { message?: string };
        throw new Error(error.message || "Failed to upload");
      }

      const data = (await res.json()) as {
        data: { url: string; type: string }[];
      };

      // Show success toast
      toast.success(files.length > 1 ? `${files.length} files uploaded` : "File uploaded", {
        id: "editor-upload",
      });

      return data.data.map((item) => ({
        url: item.url,
        type: item.type === "video" ? "video" : "image",
      })) as UploadResult[];
    },
    [authenticatedFetch]
  );

  const handleEditorUploadStart = useCallback((files: File[]) => {
    const fileNames = files.map((f) => f.name).join(", ");
    toast.loading(`Uploading ${files.length > 1 ? `${files.length} files` : fileNames}...`, {
      id: "editor-upload",
    });
  }, []);

  const handleEditorUploadError = useCallback((error: string) => {
    toast.error(error, { id: "editor-upload" });
  }, []);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (publishNow: boolean): Promise<{ data: { id: string } }> => {
      const data = {
        type: postType,
        title,
        slug: slug || slugify(title),
        content,
        excerpt: excerpt || null,
        featured_image_url: featuredImageUrl || null,
        author_name: authorName || null,
        author_avatar_url: authorAvatarUrl || null,
        seo_title: seoTitle || null,
        seo_description: seoDescription || null,
        seo_keywords: seoKeywords || null,
        status: publishNow ? "published" : "draft",
        category_ids: selectedCategories,
        // Case study specific fields
        client_name: clientName || null,
        client_logo_url: clientLogoUrl || null,
        client_industry: clientIndustry || null,
        services_used: servicesUsed.length > 0 ? JSON.stringify(servicesUsed) : null,
        testimonial_quote: testimonialQuote || null,
        testimonial_author: testimonialAuthor || null,
        testimonial_role: testimonialRole || null,
        before_image_url: beforeImageUrl || null,
        after_image_url: afterImageUrl || null,
        project_template_id: projectTemplateId || null,
        live_url: liveUrl || null,
        prompt: prompt || null,
      };

      if (isNew) {
        const res = await authenticatedFetch("/api/admin/posts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!res.ok) {
          const error = (await res.json()) as { message?: string };
          throw new Error(error.message || "Failed to create post");
        }
        return res.json() as Promise<{ data: { id: string } }>;
      } else {
        const res = await authenticatedFetch(`/api/admin/posts/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!res.ok) {
          const error = (await res.json()) as { message?: string };
          throw new Error(error.message || "Failed to update post");
        }
        return res.json() as Promise<{ data: { id: string } }>;
      }
    },
    onSuccess: (data, publishNow) => {
      queryClient.invalidateQueries({ queryKey: ["admin-posts"] });
      queryClient.invalidateQueries({ queryKey: ["admin-post-counts"] });
      setLastSaved(new Date());
      setHasUnsavedChanges(false);

      if (publishNow) {
        setStatus("published");
        toast.success("Post published successfully");
      } else {
        setStatus("draft");
        toast.success(isNew ? "Post created" : "Changes saved as draft");
      }

      if (isNew && data?.data?.id) {
        currentPostIdRef.current = data.data.id;
        navigate(`/posts/${type}/${data.data.id}`, { replace: true });
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to save post";
      toast.error(message);
    },
  });

  // Auto-save mutation (silent, no toasts)
  const autoSaveMutation = useMutation({
    mutationFn: async (): Promise<{ data: { id: string } }> => {
      const postId = currentPostIdRef.current || id;
      const isCreating = !postId || postId === "new";

      const data = {
        type: postType,
        title,
        slug: slug || slugify(title),
        content,
        excerpt: excerpt || null,
        featured_image_url: featuredImageUrl || null,
        author_name: authorName || null,
        author_avatar_url: authorAvatarUrl || null,
        seo_title: seoTitle || null,
        seo_description: seoDescription || null,
        seo_keywords: seoKeywords || null,
        status: "draft", // Auto-save always saves as draft
        category_ids: selectedCategories,
      };

      if (isCreating) {
        const res = await authenticatedFetch("/api/admin/posts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error("Auto-save failed");
        return res.json() as Promise<{ data: { id: string } }>;
      } else {
        const res = await authenticatedFetch(`/api/admin/posts/${postId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error("Auto-save failed");
        return res.json() as Promise<{ data: { id: string } }>;
      }
    },
    onMutate: () => {
      setIsAutoSaving(true);
    },
    onSuccess: (data) => {
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      setIsAutoSaving(false);

      // If this was a new post, update the URL without full navigation
      if ((!currentPostIdRef.current || id === "new") && data?.data?.id) {
        currentPostIdRef.current = data.data.id;
        window.history.replaceState(null, "", `/posts/${type}/${data.data.id}`);
      }
    },
    onError: () => {
      setIsAutoSaving(false);
      // Silent fail for auto-save - user can manually save
    },
  });

  // Auto-save effect - saves draft every 30 seconds if there are changes
  useEffect(() => {
    // Don't auto-save if there's no title (minimum requirement)
    if (!title.trim()) return;
    // Don't auto-save if no unsaved changes
    if (!hasUnsavedChanges) return;
    // Don't auto-save published posts automatically
    if (status === "published") return;
    // Don't auto-save while a save is in progress
    if (saveMutation.isPending || autoSaveMutation.isPending) return;

    const timer = setTimeout(() => {
      autoSaveMutation.mutate();
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, [
    title,
    hasUnsavedChanges,
    status,
    saveMutation.isPending,
    autoSaveMutation.isPending,
    autoSaveMutation,
  ]);

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (name: string): Promise<{ data: Category }> => {
      const res = await authenticatedFetch("/api/admin/categories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, type: postType }),
      });
      if (!res.ok) throw new Error("Failed to create category");
      return res.json() as Promise<{ data: Category }>;
    },
    onSuccess: (data: { data: Category }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      setSelectedCategories([...selectedCategories, data.data.id]);
      setNewCategoryName("");
      setShowCategoryInput(false);
      toast.success("Category created");
    },
    onError: () => {
      toast.error("Failed to create category");
    },
  });

  // Export as markdown
  const handleExport = useCallback(() => {
    const markdown = htmlToMarkdown(content);
    const frontmatter = `---
title: ${title}
slug: ${slug}
type: ${postType}
status: ${status}
author:
  name: ${authorName}
  avatar: ${authorAvatarUrl}
categories:
${selectedCategories
  .map((id) => {
    const cat = categoriesData?.data.find((c) => c.id === id);
    return `  - ${cat?.name || id}`;
  })
  .join("\n")}
featured_image: ${featuredImageUrl}
excerpt: ${excerpt}
seo:
  title: ${seoTitle}
  description: ${seoDescription}
  keywords: ${seoKeywords}
created_at: ${postData?.data?.created_at || new Date().toISOString()}
published_at: ${postData?.data?.published_at || ""}
---

`;

    const blob = new Blob([frontmatter + markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug || "untitled"}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Post exported as markdown");
  }, [
    content,
    title,
    slug,
    postType,
    status,
    authorName,
    authorAvatarUrl,
    selectedCategories,
    categoriesData,
    featuredImageUrl,
    excerpt,
    seoTitle,
    seoDescription,
    seoKeywords,
    postData,
  ]);

  const categories = categoriesData?.data || [];
  const typeLabel = type === "case-study" ? "Case Study" : "Blog Post";

  if (postLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Main editor area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <Link
              to={`/posts/${type}`}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <span className="text-sm text-muted-foreground">{typeLabel}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                status === "published" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
              }`}
            >
              {status === "published" ? "Published" : "Draft"}
            </span>
            {/* Save status indicator */}
            {isAutoSaving ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </span>
            ) : hasUnsavedChanges ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <CloudOff className="h-3 w-3" />
                Unsaved changes
              </span>
            ) : lastSaved ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Cloud className="h-3 w-3 text-success" />
                Saved {format(lastSaved, "h:mm a")}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export .md
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveMutation.mutate(false)}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Draft
            </Button>
            <Button onClick={() => saveMutation.mutate(true)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {status === "published" ? "Update" : "Publish"}
            </Button>
          </div>
        </header>

        {/* Editor */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-3xl mx-auto">
            {/* Title */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className="w-full text-4xl font-bold text-foreground bg-transparent border-0 outline-none placeholder:text-muted-foreground/50 mb-6"
            />

            {/* Content editor */}
            <TiptapEditor
              content={content}
              onChange={setContent}
              placeholder="Start writing your content..."
              onUpload={handleEditorUpload}
              onUploadStart={handleEditorUploadStart}
              onUploadError={handleEditorUploadError}
            />
          </div>
        </div>
      </div>

      {/* Settings sidebar */}
      {showSettings && (
        <aside className="w-80 border-l border-border bg-card overflow-y-auto">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-medium text-foreground">Post Settings</h2>
            <button
              onClick={() => setShowSettings(false)}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Featured Image */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Featured Image
            </label>
            {featuredImageUrl ? (
              <div className="mt-2 relative group">
                <img
                  src={featuredImageUrl}
                  alt="Featured"
                  className="w-full aspect-video object-cover rounded-lg"
                />
                <button
                  onClick={() => setFeaturedImageUrl("")}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                >
                  <X className="h-4 w-4" />
                </button>
                <ImagePickerPopover onSelect={setFeaturedImageUrl} align="end">
                  <button className="absolute bottom-2 right-2 px-2 py-1 text-xs bg-black/60 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
                    Change
                  </button>
                </ImagePickerPopover>
              </div>
            ) : (
              <ImagePickerPopover onSelect={setFeaturedImageUrl} align="start">
                <div className="mt-2 aspect-video bg-secondary border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-muted-foreground/50 transition-colors">
                  <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                  <span className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Plus className="h-3 w-3" />
                    Add featured image
                  </span>
                </div>
              </ImagePickerPopover>
            )}
          </div>

          {/* Author */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Author
            </label>
            <div className="mt-2 flex items-center gap-3">
              <ImagePickerPopover onSelect={setAuthorAvatarUrl} align="start">
                <button
                  type="button"
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-blue-400 flex items-center justify-center text-white text-sm font-medium cursor-pointer overflow-hidden hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all"
                >
                  {authorAvatarUrl ? (
                    <img
                      src={authorAvatarUrl}
                      alt={authorName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    authorName
                      ?.split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2) || "?"
                  )}
                </button>
              </ImagePickerPopover>
              <input
                type="text"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="Author name"
                className="flex-1 text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Categories
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedCategories.map((catId) => {
                const cat = categories.find((c) => c.id === catId);
                return (
                  <span
                    key={catId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary text-foreground rounded-full text-xs"
                  >
                    {cat?.singular_name || cat?.name || catId}
                    <button
                      onClick={() =>
                        setSelectedCategories(selectedCategories.filter((id) => id !== catId))
                      }
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
              {showCategoryInput ? (
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newCategoryName.trim()) {
                        createCategoryMutation.mutate(newCategoryName.trim());
                      }
                      if (e.key === "Escape") {
                        setShowCategoryInput(false);
                        setNewCategoryName("");
                      }
                    }}
                    placeholder="Category name"
                    className="text-xs px-2 py-1 border border-border rounded bg-background w-24 focus:outline-none"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      if (newCategoryName.trim()) {
                        createCategoryMutation.mutate(newCategoryName.trim());
                      }
                    }}
                    className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded"
                  >
                    Add
                  </button>
                </div>
              ) : (
                <>
                  {categories
                    .filter((c) => !selectedCategories.includes(c.id))
                    .slice(0, 3)
                    .map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategories([...selectedCategories, cat.id])}
                        className="px-2.5 py-1 border border-dashed border-border text-muted-foreground rounded-full text-xs hover:border-foreground hover:text-foreground transition-colors"
                      >
                        + {cat.name}
                      </button>
                    ))}
                  <button
                    onClick={() => setShowCategoryInput(true)}
                    className="px-2.5 py-1 border border-dashed border-border text-muted-foreground rounded-full text-xs hover:border-foreground hover:text-foreground transition-colors"
                  >
                    + New
                  </button>
                </>
              )}
            </div>
          </div>

          {/* URL Slug */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              URL Slug
            </label>
            <div className="mt-2 flex items-center">
              <span className="text-xs text-muted-foreground">
                /{type === "case-study" ? "case-studies" : "blog"}/
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(slugify(e.target.value))}
                className="flex-1 text-sm border-0 border-b border-border px-1 py-1 bg-transparent focus:outline-none focus:border-foreground"
              />
            </div>
          </div>

          {/* Excerpt */}
          <div className="p-4 border-b border-border">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Excerpt
            </label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows={3}
              placeholder="Brief summary for listings..."
              className="mt-2 w-full text-sm border border-border rounded-md px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Case Study Details - Only show for case studies */}
          {postType === "case_study" && (
            <>
              {/* Client Info */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Client
                </label>
                <div className="mt-2 flex items-center gap-3">
                  <ImagePickerPopover onSelect={setClientLogoUrl} align="start">
                    <button
                      type="button"
                      className="w-12 h-12 rounded-lg bg-secondary border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-muted-foreground/50 transition-colors overflow-hidden"
                    >
                      {clientLogoUrl ? (
                        <img
                          src={clientLogoUrl}
                          alt="Client logo"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-muted-foreground/50" />
                      )}
                    </button>
                  </ImagePickerPopover>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Client name"
                    className="flex-1 text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              {/* Industry */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Industry
                </label>
                <input
                  type="text"
                  value={clientIndustry}
                  onChange={(e) => setClientIndustry(e.target.value)}
                  placeholder="e.g., Healthcare, Fintech, E-commerce"
                  className="mt-2 w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Services Used */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Services Used
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {servicesUsed.map((service, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs"
                    >
                      {service}
                      <button
                        onClick={() => setServicesUsed(servicesUsed.filter((_, i) => i !== index))}
                        className="hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={newService}
                      onChange={(e) => setNewService(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newService.trim()) {
                          e.preventDefault();
                          setServicesUsed([...servicesUsed, newService.trim()]);
                          setNewService("");
                        }
                      }}
                      placeholder="Add service..."
                      className="w-24 text-xs border border-dashed border-border rounded-full px-2.5 py-1 bg-transparent focus:outline-none focus:border-muted-foreground"
                    />
                    {newService.trim() && (
                      <button
                        onClick={() => {
                          setServicesUsed([...servicesUsed, newService.trim()]);
                          setNewService("");
                        }}
                        className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-full hover:bg-primary/90"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Live URL */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Live URL
                </label>
                <input
                  type="url"
                  value={liveUrl}
                  onChange={(e) => setLiveUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="mt-2 w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Link to the live website for this case study
                </p>
              </div>

              {/* Build It Prompt */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Build It Prompt
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Enter a prompt for users to create a similar project..."
                  className="mt-2 w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none h-20"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Displayed on the case study page to guide users starting their own project
                </p>
              </div>

              {/* Testimonial */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Client Testimonial
                </label>
                <textarea
                  value={testimonialQuote}
                  onChange={(e) => setTestimonialQuote(e.target.value)}
                  rows={3}
                  placeholder="What did your client say?"
                  className="mt-2 w-full text-sm border border-border rounded-md px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={testimonialAuthor}
                    onChange={(e) => setTestimonialAuthor(e.target.value)}
                    placeholder="Author name"
                    className="text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <input
                    type="text"
                    value={testimonialRole}
                    onChange={(e) => setTestimonialRole(e.target.value)}
                    placeholder="Role/Title"
                    className="text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              {/* Before/After */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Before & After
                </label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <ImagePickerPopover onSelect={setBeforeImageUrl} align="start">
                      <div className="aspect-video bg-secondary border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-muted-foreground/50 transition-colors overflow-hidden">
                        {beforeImageUrl ? (
                          <img
                            src={beforeImageUrl}
                            alt="Before"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <>
                            <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                            <span className="text-xs text-muted-foreground mt-1">Before</span>
                          </>
                        )}
                      </div>
                    </ImagePickerPopover>
                    {beforeImageUrl && (
                      <button
                        onClick={() => setBeforeImageUrl("")}
                        className="mt-1 text-xs text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div>
                    <ImagePickerPopover onSelect={setAfterImageUrl} align="end">
                      <div className="aspect-video bg-secondary border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-muted-foreground/50 transition-colors overflow-hidden">
                        {afterImageUrl ? (
                          <img
                            src={afterImageUrl}
                            alt="After"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <>
                            <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                            <span className="text-xs text-muted-foreground mt-1">After</span>
                          </>
                        )}
                      </div>
                    </ImagePickerPopover>
                    {afterImageUrl && (
                      <button
                        onClick={() => setAfterImageUrl("")}
                        className="mt-1 text-xs text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Template ID */}
              <div className="p-4 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Project Template ID
                </label>
                <input
                  type="text"
                  value={projectTemplateId}
                  onChange={(e) => setProjectTemplateId(e.target.value)}
                  placeholder="e.g., tmpl_abc123"
                  className="mt-2 w-full text-sm border border-border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </>
          )}

          {/* SEO Settings */}
          <div className="p-4">
            <button
              onClick={() => setShowSeo(!showSeo)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                SEO Settings
              </span>
              {showSeo ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
            {showSeo && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">Meta Title</label>
                  <input
                    type="text"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder={title || "Page title"}
                    className="w-full text-sm border border-border rounded-md px-3 py-1.5 mt-1 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Meta Description</label>
                  <textarea
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    rows={2}
                    placeholder="Brief description for search engines..."
                    className="w-full text-sm border border-border rounded-md px-3 py-1.5 mt-1 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Keywords</label>
                  <input
                    type="text"
                    value={seoKeywords}
                    onChange={(e) => setSeoKeywords(e.target.value)}
                    placeholder="keyword1, keyword2, keyword3"
                    className="w-full text-sm border border-border rounded-md px-3 py-1.5 mt-1 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
